import React, {useCallback, useMemo, useState} from 'react';
import {Image, Spinner} from 'react-bootstrap';
import {parseHtmlString} from 'shared/helpers/HTMLParser';
import {ItemColumn} from 'shared/components/Product/ItemColumn';
import styled, {css} from 'styled-components';
import {lightenColor} from 'components/customer/Confirmation/helpers/lightenColor';
import {Icon} from 'shared/helpers';
import {mobileLandscape, mobilePortrait} from 'shared/Breakpoints';
import {FAVOURITE_CATEGORY_NAME} from 'hooks/QuickProduct/ProductSearch';
import {FavouriteSelected} from 'assets';
import {SHOW_ALL_BUTTON} from 'hooks/Product/Product';

export interface CategoryInterface {
    id: number;
    name: string;
    imageUrl: string;
    action: () => void;
    noImage?: boolean;
    isSubCategory?: boolean;
    productCount?: number;
    description?: string;
    /**
     * How to fit a placeholder thumbnail. Set with a bundled `imageUrl`.
     * Cabinet categories leave this unset and keep cover.
     */
    iconFit?: 'contain' | 'cover';
}

interface CategoryProps {
    category: CategoryInterface;
    categoryClickHandler: (category: CategoryInterface) => void;
    isProduct: boolean;
    isMiniBrowser?: boolean;
}

export const Category = ({
    category,
    categoryClickHandler,
    isProduct,
    isMiniBrowser = false,
}: CategoryProps) => {
    const [imageFailed, setImageFailed] = useState(false);
    const [imageLoading, setImageLoading] = useState(true);

    const handleClick = useCallback(() => {
        if (category.hasOwnProperty('noImage') && category.noImage) {
            category.action();
        } else {
            categoryClickHandler(category);
        }
    }, [category, categoryClickHandler]);

    const onErrorHandler = useCallback(() => {
        setImageFailed(true);
        setImageLoading(false);
    }, []);

    const onLoadHandler = useCallback(() => {
        setImageLoading(false);
    }, []);

    const imageContainerStyle = isProduct ? {background: '#fff'} : {};
    // Only placeholder cards set this. Their thumbnail is already a full
    // address, so it is used as-is. Cabinet paths keep the leading slash.
    const fitPlaceholderThumb = category.iconFit === 'contain';

    const isFavourite = category.name == FAVOURITE_CATEGORY_NAME;
    const isShowAll = category.name == SHOW_ALL_BUTTON;

    const image = useMemo(() => {
        if (isFavourite) {
            return String(FavouriteSelected);
        }

        return `/${category.imageUrl}`;
    }, [category.imageUrl, isFavourite]);

    return (
        <ItemColumn>
            <Container onClick={handleClick}>
                <div>
                    <ImageContainer
                        $isShowAll={isShowAll}
                        $isMiniBrowser={isMiniBrowser}
                        $padded={fitPlaceholderThumb}
                        className="imageContainer"
                        style={imageContainerStyle}>
                        {isShowAll ? (
                            <Text>SHOW ALL</Text>
                        ) : (
                            <>
                                <HiddenSpinner
                                    $hide={!imageLoading}
                                    animation="border"
                                    role="status"
                                    variant="dark">
                                    <span className="visually-hidden">
                                        Loading...
                                    </span>
                                </HiddenSpinner>
                                {imageFailed ? (
                                    <ErrorIcon iconName="Stack.svg" />
                                ) : (
                                    <HiddenImage
                                        $hide={imageLoading}
                                        $isFavourite={isFavourite}
                                        $fitContain={fitPlaceholderThumb}
                                        src={image}
                                        alt={category.name}
                                        style={{
                                            objectFit: fitPlaceholderThumb
                                                ? 'contain'
                                                : isFavourite
                                                  ? 'none'
                                                  : 'cover',
                                        }}
                                        onError={onErrorHandler}
                                        onLoad={onLoadHandler}
                                    />
                                )}
                            </>
                        )}
                    </ImageContainer>
                    <CategoryName
                        $isMiniBrowser={isMiniBrowser}
                        className="categoryName">
                        {!isShowAll ? (
                            <Name>{parseHtmlString(category.name)}</Name>
                        ) : null}
                        {category.description ? (
                            <Description title={category.description}>
                                {category.description}
                            </Description>
                        ) : null}
                    </CategoryName>
                </div>
                <Footer $isMiniBrowser={isMiniBrowser}>
                    <span>View products</span>
                    <span>
                        {category.productCount && category.productCount > 0 ? (
                            <>
                                ({category.productCount}{' '}
                                {category.productCount > 1 ? 'items' : 'item'})
                            </>
                        ) : null}
                    </span>
                </Footer>
            </Container>
        </ItemColumn>
    );
};

const Text = styled.div`
    font-size: 2em;
    font-weight: 500;
    color: white;
    text-align: center;
    line-height: 1em;
`;

const ErrorIcon = styled(Icon)`
    width: 20% !important;
`;

const Description = styled.div`
    font-size: 0.8em;
    color: #4b4a4a;
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    overflow: hidden;
    height: 72px;
`;

interface HiddenProps {
    $hide: boolean;
    $isFavourite?: boolean;
    $fitContain?: boolean;
}

const hideElement = css<HiddenProps>`
    ${({$hide}) => {
        if ($hide) {
            return `display: none`;
        }
    }}
`;

const HiddenSpinner = styled(Spinner)<HiddenProps>`
    ${hideElement}
`;

const HiddenImage = styled(Image)<HiddenProps>`
    ${hideElement}

    ${({$isFavourite}) => {
        if ($isFavourite) {
            return `
                width: 49px !important;
                height: 60px;
            `;
        }
    }}

    ${({$fitContain}) => {
        if ($fitContain) {
            // The card rule sets img { width: 100% }. On a tablet that makes
            // a short, wide frame, so a tall photo is clipped. Size to the
            // frame instead, on phone and tablet.
            return `
                width: auto !important;
                height: auto !important;
                max-width: 100%;
                max-height: 100%;
                object-fit: contain !important;
            `;
        }
    }}
`;

const Container = styled.div`
    position: relative;
    border: 1px solid ${({theme}) => theme.colors.secondary?.main};
    border-radius: 8px;
    margin-top: 10px;
    z-index: 1;
    cursor: pointer;
    height: calc(100% - 10px);
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    &:before {
        content: '';
        position: absolute;
        width: 90%;
        margin: 0 auto;
        height: 16px;
        border: 1px solid
            ${({theme}) => lightenColor(theme.colors.secondary?.main, 45)};
        border-bottom: 0;
        border-radius: 8px;
        background: ${({theme}) => lightenColor(theme.colors.secondary?.main, 85)};
        top: -8px;
        left: 0;
        right: 0;
        z-index: -1;
    }

    > div:first-child {
        border-top: 1px solid ${({theme}) => theme.colors.secondary?.main};
        border-top-left-radius: 6px;
        border-top-right-radius: 6px;
        display: flex;
        flex-direction: column;
        background: white;
        min-height: 131px;
        padding: 10px;
        gap: 10px;
        margin-top: -1px;
        align-items: flex-start;
    }

    > div > div > img {
        width: 100%;
    }
`;

const SmallBrowser = css`
    flex-direction: column-reverse;
    align-items: flex-end;
`;

const Footer = styled.div<{$isMiniBrowser: boolean}>`
    color: ${({theme}) => theme.colors.secondary?.main};
    background: ${({theme}) => lightenColor(theme.colors.secondary?.main, 85)};
    padding: 5px 10px;
    display: flex;
    justify-content: space-between;
    font-size: 0.8em;
    font-weight: bold;
    border-top: 1px solid
        ${({theme}) => lightenColor(theme.colors.secondary?.main, 45)};
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
    cursor: pointer;

    > span:first-child {
        font-weight: 400;
    }

    ${({$isMiniBrowser}) => {
        if ($isMiniBrowser) {
            return SmallBrowser;
        }
    }}

    ${mobilePortrait`${SmallBrowser}`}
    ${mobileLandscape`${SmallBrowser}`};
`;

export const ImageContainer = styled.div<{
    $isMiniBrowser: boolean;
    $padded?: boolean;
    $isShowAll: boolean;
}>`
    border: 1px solid #d5d5d5;
    border-radius: 5px;
    display: flex;
    align-items: center;
    justify-content: center;
    height: fit-content;
    overflow: hidden;
    height: 149px;
    width: 100%;

    ${({$padded}) => {
        if ($padded) {
            return 'padding: 18px;';
        }
    }}

    ${({$isMiniBrowser}) => {
        if ($isMiniBrowser) {
            return 'height: 100px !important;';
        }
    }}

    ${({$isShowAll, theme}) => {
        if ($isShowAll) {
            return `background: ${theme.colors.secondary?.main} !important;`;
        }
    }}
`;

export const CategoryName = styled.div<{
    $isMiniBrowser: boolean;
}>`
    flex: 1;
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    flex-direction: column;
`;

export const Name = styled.div`
    font-size: 0.9em;
    font-weight: 500;
`;

export const Count = styled.div`
    font-size: 0.85em;
    color: gray;
    display: inline;
    padding: 2px 10px;
    background: ${({theme}) => lightenColor(theme.colors.secondary?.main, 85)};
    margin-left: 10px;
    border: 1px solid ${({theme}) => theme.colors.secondary?.main};
    border-radius: 19px;
`;
